import isTesting from 'nightwatch-web/utils/is-testing';
import Service, { inject as service } from '@ember/service';
import { isPresent } from '@ember/utils';
import md5 from 'js-md5';
import { task, rawTimeout } from 'ember-concurrency';
import {
  isServerErrorResponse,
  isUnauthorizedResponse,
} from 'ember-fetch/errors';
import { tracked } from '@glimmer/tracking';
import posthog from 'posthog-js';
import * as Sentry from '@sentry/ember';

const AFTER_LOGOUT_URL = 'login';

const TOKEN_KEY = 'nw:session:token';
const FROM_ADMIN_KEY = 'nw:session:from-admin';
const TRACK_REGISTERED_KEY = 'nw:session:track-registered';

const REQUIRE_AUTHENTICATION_ROUTE = 'login';
const PROHIBIT_AUTHENTICATION_ROUTE = 'dashboard.overview';

export default class SessionService extends Service {
  @service store;
  @service siteData;
  @service metrics;
  @service intercom;
  @service fetch;
  @service router;
  @service theme;
  @service cookie;

  @tracked user;

  get isAuthenticated() {
    return isPresent(this.token);
  }

  get token() {
    return this.cookie.getCookie(TOKEN_KEY);
  }
  set token(value) {
    if (value !== null) {
      this.cookie.setCookie(TOKEN_KEY, value, 30); // 30 days
    } else {
      this.cookie.deleteCookie(TOKEN_KEY);
    }
  }

  get isAdminViewingUser() {
    const value = this.cookie.getCookie(FROM_ADMIN_KEY);
    return value ? JSON.parse(value) : null;
  }
  set isAdminViewingUser(value) {
    // Disable all metrics for token authenticated sessions
    this.metrics.enabled = value !== true;

    if (value !== null) {
      this.cookie.setCookie(FROM_ADMIN_KEY, JSON.stringify(value), 30);
    } else {
      this.cookie.deleteCookie(FROM_ADMIN_KEY);
    }
  }

  get trackRegistered() {
    const value = this.cookie.getCookie(TRACK_REGISTERED_KEY);
    return value ? JSON.parse(value) : null;
  }
  set trackRegistered(value) {
    if (value !== null) {
      this.cookie.setCookie(TRACK_REGISTERED_KEY, JSON.stringify(value), 30);
    } else {
      this.cookie.deleteCookie(TRACK_REGISTERED_KEY);
    }
  }

  @task({ drop: true })
  *trackLogin() {
    // It's important that the "Registered" event is provided to Intercom which is only booted after login
    if (this.trackRegistered) {
      this.metrics.trackEvent({ event: 'Registered' });
      this.trackRegistered = null;
      return;
    }
    yield rawTimeout(1000);
    this.metrics.trackEvent({ event: 'Logged In' });
  }

  @task({ drop: true })
  *login({ email, password, skipRedirect }) {
    const data = {
      email,
      password,
      beta_signup_token: md5(decodeURIComponent(email)).slice(-10),
    };

    let response;

    try {
      response = yield this.fetch.post('/token', { data });
    } catch (error) {
      return this.handleLoginError(error);
    }

    this.token = response.access_token;
    this.isAdminViewingUser = null;
    if (isTesting || skipRedirect) return;
    yield this.loadUser.perform();
    this.router.transitionTo('dashboard.overview');
  }

  handleLoginError(error) {
    if (isServerErrorResponse(error) && error.status === 503) {
      this.router.transitionTo('maintenance');
      return;
    }

    if (isUnauthorizedResponse(error)) {
      let explanation = error?.payload?.explanation;
      if (explanation?.match(/Your plan does not support API access/))
        explanation = 'Login not allowed. Please contact support.';
      throw explanation ?? 'Incorrect e-mail or password.';
    }

    throw error;
  }

  logout(redirectToLogin = true) {
    this.metrics.trackEvent({ event: 'Logged Out' });
    this.intercom.shutdown();

    this.invalidate();
    this.user = null;
    Sentry.setUser(null);

    if (redirectToLogin) {
      this.router.transitionTo(AFTER_LOGOUT_URL);
    }
  }

  checkConfirmed() {
    if (this.user.id && !this.user.confirmed) {
      this.router.transitionTo('confirm-account');
    }
  }
  redirectUser(token) {
    if (token) {
      if (this.user.isOwner) {
        this.router.transitionTo('onboarding');
      } else {
        this.router.transitionTo('dashboard.overview');
      }
    }
  }

  @task({ drop: true })
  *loadUser(token) {
    if (this.user) {
      this.redirectUser(token);
      return this.user;
    }

    if (token) {
      this.token = token;
    }

    const users = yield this.store.query('user', { without_stats: true });

    this.user = users.firstObject;
    this.checkConfirmed();
    this.siteData.afterLoadUser(this.user);
    this.theme.afterLoadUser(this.user);
    posthog.identify(
      this.user.id, // distinct_id, required
      { email: this.user.email } // $set, optional
    );

    Sentry.setUser({ id: this.user.id, email: this.user.email });

    this.redirectUser(token);
    return this.user;
  }

  ingestQueryParamToken(token) {
    if (token !== this.token) {
      this.intercom.shutdown();
    }
    this.token = token;
    // Persist - in case an admin visits the site with some elses token
    // without the t parameter
    this.isAdminViewingUser = true;
  }

  invalidate() {
    this.token = null;
    this.isAdminViewingUser = null;
  }

  requireActive() {
    if (this.user?.isInactive) this.router.transitionTo('plans');
  }

  requireAuthentication(route) {
    if (this.isAuthenticated) return;

    this.router.transitionTo(route || REQUIRE_AUTHENTICATION_ROUTE);
  }

  prohibitAuthentication(route) {
    if (!this.isAuthenticated) return;

    this.router.transitionTo(route || PROHIBIT_AUTHENTICATION_ROUTE);
  }
}
